import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import { useAuth } from "../../../../context/AuthContext";
import {
  ConversationMessageDto,
  ConversationSummaryDto,
  getConversationMessages,
  getConversations,
  markConversationRead,
  sendConversationReply,
} from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import "../../../../css/buttons.css";
import "../../../../css/forms.css";
import "../../../../css/layout.css";
import "./ConversationInboxPage.css";

const REFRESH_INTERVAL_MS = 15_000;
const MAX_REPLY_LENGTH = 1_600;

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();

  return sameDay
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const normaliseDirection = (direction: string) =>
  direction.toLowerCase() === "inbound" ? "inbound" : "outbound";

export default function ConversationInboxPage() {
  const { canSendCommunications } = useAuth();
  const [conversations, setConversations] = useState<
    ConversationSummaryDto[]
  >([]);
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | null
  >(null);
  const [messages, setMessages] = useState<ConversationMessageDto[]>([]);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [reply, setReply] = useState("");
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) =>
          conversation.conversationId === selectedConversationId,
      ) ?? null,
    [conversations, selectedConversationId],
  );

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    return conversations.filter((conversation) => {
      if (unreadOnly && conversation.unreadCount === 0) return false;
      if (!term) return true;

      return `${conversation.partyName} ${conversation.remoteAddress} ${conversation.lastMessagePreview}`
        .toLowerCase()
        .includes(term);
    });
  }, [conversations, search, unreadOnly]);

  const loadInbox = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingInbox(true);

      try {
        const data = await getConversations(false, silent);
        setConversations(data);
        setError(null);
        setSelectedConversationId((current) => {
          if (
            current &&
            data.some((conversation) => conversation.conversationId === current)
          ) {
            return current;
          }

          return data[0]?.conversationId ?? null;
        });
      } catch {
        if (!silent) {
          setConversations([]);
          setSelectedConversationId(null);
          setError("Conversations could not be loaded.");
        }
      } finally {
        if (!silent) setLoadingInbox(false);
      }
    },
    [],
  );

  const loadMessages = useCallback(
    async (conversationId: number, silent = false) => {
      if (!silent) setLoadingMessages(true);

      try {
        const data = await getConversationMessages(conversationId, silent);
        setMessages(data);
        setError(null);
      } catch {
        if (!silent) {
          setMessages([]);
          setError("This conversation could not be loaded.");
        }
      } finally {
        if (!silent) setLoadingMessages(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }

    void loadMessages(selectedConversationId);
    void markConversationRead(selectedConversationId)
      .then(() => {
        setConversations((current) =>
          current.map((conversation) =>
            conversation.conversationId === selectedConversationId
              ? { ...conversation, unreadCount: 0 }
              : conversation,
          ),
        );
      })
      .catch(() => undefined);
  }, [loadMessages, selectedConversationId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const frame = window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [messages, selectedConversationId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadInbox(true);
      if (selectedConversationId) {
        void loadMessages(selectedConversationId, true);
      }
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [loadInbox, loadMessages, selectedConversationId]);

  const selectConversation = (conversationId: number) => {
    setReply("");
    setSelectedConversationId(conversationId);
  };

  const sendReply = async (event?: FormEvent) => {
    event?.preventDefault();

    const messageBody = reply.trim();
    if (!selectedConversationId || !messageBody || sending) return;

    setSending(true);
    try {
      await sendConversationReply(selectedConversationId, messageBody);
      setReply("");
      await Promise.all([
        loadMessages(selectedConversationId, true),
        loadInbox(true),
      ]);
    } catch {
      setError("The SMS reply could not be queued.");
    } finally {
      setSending(false);
    }
  };

  const handleReplyKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void sendReply();
    }
  };

  return (
    <div className="page-container conversation-page">
      <PageHeader
        title="Conversations"
        subtitle="Read and reply to SMS conversations"
        actions={
          <button
            type="button"
            className="icon-button-muted-solid"
            onClick={() => void loadInbox()}
            title="Refresh conversations"
            aria-label="Refresh conversations"
          >
            <RefreshIcon fontSize="small" />
          </button>
        }
      />

      {error && (
        <div className="conversation-alert" role="alert">
          {error}
        </div>
      )}

      <div
        className={`conversation-workspace ${
          selectedConversation ? "conversation-workspace--thread-open" : ""
        }`}
      >
        <aside className="conversation-inbox" aria-label="SMS conversations">
          <div className="conversation-inbox-toolbar">
            <label className="conversation-search">
              <SearchIcon fontSize="small" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search conversations"
                aria-label="Search conversations"
              />
            </label>

            <label className="conversation-unread-filter">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(event) => setUnreadOnly(event.target.checked)}
              />
              Unread only
            </label>
          </div>

          <div className="conversation-list">
            {loadingInbox ? (
              <div className="conversation-empty">Loading conversations…</div>
            ) : filteredConversations.length === 0 ? (
              <div className="conversation-empty">
                {search
                  ? "No conversations match your search."
                  : unreadOnly
                    ? "You have no unread conversations."
                    : "No SMS conversations yet. Send a new campaign to begin testing."}
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <button
                  type="button"
                  key={conversation.conversationId}
                  className={`conversation-list-item ${
                    selectedConversationId === conversation.conversationId
                      ? "conversation-list-item--selected"
                      : ""
                  }`}
                  onClick={() =>
                    selectConversation(conversation.conversationId)
                  }
                  aria-current={
                    selectedConversationId === conversation.conversationId
                      ? "true"
                      : undefined
                  }
                >
                  <span className="conversation-avatar" aria-hidden="true">
                    <ChatBubbleOutlineIcon fontSize="small" />
                  </span>
                  <span className="conversation-list-copy">
                    <span className="conversation-list-heading">
                      <strong>
                        {conversation.partyName || conversation.remoteAddress}
                      </strong>
                      <time dateTime={conversation.lastMessageDate}>
                        {formatDateTime(conversation.lastMessageDate)}
                      </time>
                    </span>
                    <span className="conversation-address">
                      {conversation.remoteAddress} · {conversation.channelType}
                    </span>
                    <span className="conversation-preview">
                      {conversation.lastMessagePreview}
                    </span>
                  </span>
                  {conversation.unreadCount > 0 && (
                    <span
                      className="conversation-unread-count"
                      aria-label={`${conversation.unreadCount} unread messages`}
                    >
                      {conversation.unreadCount > 99
                        ? "99+"
                        : conversation.unreadCount}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </aside>

        <section className="conversation-thread" aria-live="polite">
          {!selectedConversation ? (
            <div className="conversation-thread-placeholder">
              <ChatBubbleOutlineIcon />
              <h2>Select a conversation</h2>
              <p>Choose an SMS conversation to read and reply.</p>
            </div>
          ) : (
            <>
              <header className="conversation-thread-header">
                <button
                  type="button"
                  className="conversation-mobile-back"
                  onClick={() => setSelectedConversationId(null)}
                  aria-label="Back to conversations"
                >
                  <ArrowBackIcon fontSize="small" />
                </button>
                <div>
                  <h2>
                    {selectedConversation.partyName ||
                      selectedConversation.remoteAddress}
                  </h2>
                  <p>
                    {selectedConversation.remoteAddress} · SMS
                    <span className="conversation-status">
                      {selectedConversation.status}
                    </span>
                  </p>
                </div>
              </header>

              <div
                className="conversation-messages"
                ref={messagesContainerRef}
              >
                {loadingMessages ? (
                  <div className="conversation-empty">
                    Loading messages…
                  </div>
                ) : messages.length === 0 ? (
                  <div className="conversation-empty">
                    No messages in this conversation.
                  </div>
                ) : (
                  messages.map((message) => {
                    const direction = normaliseDirection(message.direction);
                    return (
                      <article
                        key={message.conversationMessageId}
                        className={`conversation-message conversation-message--${direction}`}
                      >
                        <div className="conversation-message-bubble">
                          <p>{message.messageBody}</p>
                          <footer>
                            <time dateTime={message.messageDate}>
                              {formatDateTime(message.messageDate)}
                            </time>
                            {direction === "outbound" && (
                              <span>{message.status}</span>
                            )}
                          </footer>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>

              {canSendCommunications ? <form className="conversation-reply" onSubmit={sendReply}>
                <label htmlFor="conversation-reply-message">
                  Reply by SMS
                </label>
                <div className="conversation-reply-controls">
                  <textarea
                    id="conversation-reply-message"
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    onKeyDown={handleReplyKeyDown}
                    maxLength={MAX_REPLY_LENGTH}
                    rows={3}
                    placeholder="Write a reply…"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    className="conversation-send-button"
                    disabled={!reply.trim() || sending}
                  >
                    <SendIcon fontSize="small" />
                    <span>{sending ? "Sending…" : "Send SMS"}</span>
                  </button>
                </div>
                <div className="conversation-reply-help">
                  <span>Ctrl + Enter to send</span>
                  <span>
                    {reply.length.toLocaleString()} /{" "}
                    {MAX_REPLY_LENGTH.toLocaleString()}
                  </span>
                </div>
              </form> : <div className="field-hint">You can view this conversation but do not have permission to send communications.</div>}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
